import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Calendar, Users, Activity, Save, Plus, Search, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_LABELS = { assessment: 'Assessment', preparation: 'Preparation', execution: 'Execution', landing: 'Landing', settled: 'Settled' };
const VISA_OPTIONS = ['not_started', 'in_progress', 'submitted', 'approved'];
const HOUSING_OPTIONS = ['searching', 'shortlisted', 'secured'];
const BANKING_OPTIONS = ['not_started', 'wise_setup', 'local_account_opened'];
const PROPERTY_OPTIONS = ['not_started', 'active', 'under_contract', 'closed'];
const TIER_OPTIONS = ['self_serve', 'kickstart', 'full_coordination'];
const TIER_LABELS = { self_serve: 'Self Serve', kickstart: 'Kickstart', full_coordination: 'Full Coordination' };

const StatusBadge = ({ value, options, onChange, colors }) => (
  <select value={value || options[0]} onChange={(e) => onChange(e.target.value)} className="text-xs px-2 py-1 rounded border border-[#333] bg-[#111] text-white appearance-none cursor-pointer" data-testid="status-select">
    {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
  </select>
);

function DailyDashboard({ dashboard }) {
  if (!dashboard) return null;
  return (
    <div className="mb-8 space-y-4" data-testid="daily-dashboard">
      <h2 className="text-lg text-[#0A1F2E] font-medium">Daily Dashboard</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#111] border border-[#FFFFFF] p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-400" /><span className="text-[#0A1F2E]/60 text-xs">Needs Attention</span></div>
          <span className="text-2xl text-white font-light" data-testid="needs-attention-count">{dashboard.needs_attention?.length || 0}</span>
        </div>
        <div className="bg-[#111] border border-[#FFFFFF] p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><Calendar className="w-4 h-4 text-[#2CA8A0]" /><span className="text-[#0A1F2E]/60 text-xs">Sessions This Week</span></div>
          <span className="text-2xl text-white font-light" data-testid="sessions-count">{dashboard.upcoming_sessions?.length || 0}</span>
        </div>
        <div className="bg-[#111] border border-[#FFFFFF] p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-blue-400" /><span className="text-[#0A1F2E]/60 text-xs">Total Clients</span></div>
          <span className="text-2xl text-white font-light" data-testid="total-clients">{dashboard.total_clients || 0}</span>
        </div>
        <div className="bg-[#111] border border-[#FFFFFF] p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-orange-400" /><span className="text-[#0A1F2E]/60 text-xs">Active Pipeline</span></div>
          <span className="text-2xl text-white font-light" data-testid="pipeline-count">{dashboard.pipeline?.length || 0}</span>
        </div>
      </div>
      {/* Phase breakdown */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(dashboard.phase_breakdown || {}).map(([phase, count]) => (
          <span key={phase} className="text-xs bg-[#FFFFFF] text-[#888] px-3 py-1.5 rounded-full">{PHASE_LABELS[phase] || phase}: <span className="text-[#0A1F2E] font-medium">{count}</span></span>
        ))}
      </div>
      {/* Flagged clients */}
      {dashboard.needs_attention?.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400 text-xs font-medium mb-2">Clients needing attention (7+ days no contact):</p>
          {dashboard.needs_attention.map(c => (
            <p key={c.client_id} className="text-[#0A1F2E]/60 text-xs">{c.name} - {c.days_since_contact} days, {PHASE_LABELS[c.phase]}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ client, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const dirty = Object.keys(edits).length > 0;

  const val = (key) => edits[key] !== undefined ? edits[key] : client[key];
  const edit = (key, v) => setEdits(prev => ({ ...prev, [key]: v }));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/command-center/clients/${client.client_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(edits),
      });
      if (res.ok) { const { client: updated } = await res.json(); onSave(updated); setEdits({}); }
    } finally { setSaving(false); }
  };

  const daysSince = client.days_since_contact;
  const redFlag = client.red_flag;

  return (
    <div className={`border rounded-lg ${redFlag ? 'border-red-500/40 bg-red-500/5' : 'border-[#FFFFFF] bg-[#111]'}`} data-testid={`client-card-${client.client_id}`}>
      <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          {redFlag && <AlertTriangle className="w-4 h-4 text-red-400" />}
          <div>
            <span className="text-[#0A1F2E] font-medium text-sm">{client.name}</span>
            <span className="text-[#666] text-xs ml-2">{client.email}</span>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${client.tier === 'full_coordination' ? 'bg-[#B8860B]/20 text-[#B8860B]' : client.tier === 'kickstart' ? 'bg-blue-500/20 text-blue-400' : 'bg-[#333] text-[#888]'}`}>{TIER_LABELS[client.tier] || client.tier}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#0A1F2E]/60 text-xs">{PHASE_LABELS[client.phase]}</span>
          {daysSince !== null && <span className={`text-xs ${daysSince >= 7 ? 'text-red-400' : 'text-[#666]'}`}>{daysSince}d ago</span>}
          {expanded ? <ChevronUp className="w-4 h-4 text-[#666]" /> : <ChevronDown className="w-4 h-4 text-[#666]" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#FFFFFF] p-4 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-[#666] text-xs block mb-1">Phase</label>
              <select value={val('phase')} onChange={e => edit('phase', e.target.value)} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-2 py-1.5 rounded" data-testid="edit-phase">
                {Object.entries(PHASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[#666] text-xs block mb-1">City</label>
              <input value={val('city') || ''} onChange={e => edit('city', e.target.value)} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-2 py-1.5 rounded" data-testid="edit-city" />
            </div>
            <div>
              <label className="text-[#666] text-xs block mb-1">Tier</label>
              <select value={val('tier')} onChange={e => edit('tier', e.target.value)} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-2 py-1.5 rounded" data-testid="edit-tier">
                {TIER_OPTIONS.map(t => <option key={t} value={t}>{TIER_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[#666] text-xs block mb-1">Last Contact</label>
              <input type="date" value={(val('last_contact') || '').slice(0, 10)} onChange={e => edit('last_contact', new Date(e.target.value).toISOString())} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-2 py-1.5 rounded" data-testid="edit-last-contact" />
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div><label className="text-[#666] text-xs block mb-1">Visa</label><StatusBadge value={val('visa_status')} options={VISA_OPTIONS} onChange={v => edit('visa_status', v)} /></div>
            <div><label className="text-[#666] text-xs block mb-1">Housing</label><StatusBadge value={val('housing_status')} options={HOUSING_OPTIONS} onChange={v => edit('housing_status', v)} /></div>
            <div><label className="text-[#666] text-xs block mb-1">Banking</label><StatusBadge value={val('banking_status')} options={BANKING_OPTIONS} onChange={v => edit('banking_status', v)} /></div>
            <div><label className="text-[#666] text-xs block mb-1">Property</label><StatusBadge value={val('property_status')} options={PROPERTY_OPTIONS} onChange={v => edit('property_status', v)} /></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div><label className="text-[#666] text-xs block mb-1">Last Session</label><input type="date" value={(val('last_session') || '').slice(0, 10)} onChange={e => edit('last_session', e.target.value)} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-2 py-1.5 rounded" data-testid="edit-last-session" /></div>
            <div><label className="text-[#666] text-xs block mb-1">Next Session</label><input type="date" value={(val('next_session') || '').slice(0, 10)} onChange={e => edit('next_session', e.target.value)} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-2 py-1.5 rounded" data-testid="edit-next-session" /></div>
          </div>

          <div>
            <label className="text-[#666] text-xs block mb-1">Session Notes</label>
            <textarea value={val('session_notes') || ''} onChange={e => edit('session_notes', e.target.value)} rows={3} className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-3 py-2 rounded resize-none" data-testid="edit-notes" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <label className="text-[#666] text-xs block mb-1">Advisor Actions</label>
              <textarea value={(val('francisco_actions') || []).join('\n')} onChange={e => edit('francisco_actions', e.target.value.split('\n').filter(Boolean))} rows={3} placeholder="One action per line" className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-3 py-2 rounded resize-none" data-testid="edit-francisco-actions" />
            </div>
            <div>
              <label className="text-[#666] text-xs block mb-1">Client's Actions</label>
              <textarea value={(val('client_actions') || []).join('\n')} onChange={e => edit('client_actions', e.target.value.split('\n').filter(Boolean))} rows={3} placeholder="One action per line" className="w-full text-xs bg-[#F9F6EF] border border-[#333] text-white px-3 py-2 rounded resize-none" data-testid="edit-client-actions" />
            </div>
          </div>

          {dirty && (
            <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#2CA8A0] text-black px-4 py-2 text-xs font-medium rounded hover:bg-[#249089]" data-testid="save-client-btn">
              <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CommandCenter() {
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', tier: 'self_serve', city: '' });

  const load = useCallback(async () => {
    const [cRes, dRes] = await Promise.all([
      fetch(`${API_URL}/api/admin/command-center/clients`),
      fetch(`${API_URL}/api/admin/command-center/dashboard`),
    ]);
    if (cRes.ok) { const d = await cRes.json(); setClients(d.clients); }
    if (dRes.ok) { const d = await dRes.json(); setDashboard(d); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addClient = async () => {
    if (!newClient.name || !newClient.email) return;
    const res = await fetch(`${API_URL}/api/admin/command-center/clients`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newClient),
    });
    if (res.ok) { setShowAdd(false); setNewClient({ name: '', email: '', tier: 'self_serve', city: '' }); load(); }
  };

  const handleSave = (updated) => {
    setClients(prev => prev.map(c => c.client_id === updated.client_id ? updated : c));
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.city?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="command-center">
      <DailyDashboard dashboard={dashboard} />

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." className="w-full bg-[#111] border border-[#FFFFFF] text-white text-sm pl-10 pr-4 py-2.5 rounded-lg" data-testid="client-search" />
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 bg-[#2CA8A0] text-black px-4 py-2.5 text-sm font-medium rounded-lg hover:bg-[#249089]" data-testid="add-client-btn">
          <Plus className="w-4 h-4" /> Add Client
        </button>
      </div>

      {showAdd && (
        <div className="bg-[#111] border border-[#FFFFFF] rounded-lg p-4 space-y-3" data-testid="add-client-form">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <input value={newClient.name} onChange={e => setNewClient({ ...newClient, name: e.target.value })} placeholder="Full name" className="bg-[#F9F6EF] border border-[#333] text-white text-xs px-3 py-2 rounded" data-testid="new-client-name" />
            <input value={newClient.email} onChange={e => setNewClient({ ...newClient, email: e.target.value })} placeholder="Email" className="bg-[#F9F6EF] border border-[#333] text-white text-xs px-3 py-2 rounded" data-testid="new-client-email" />
            <select value={newClient.tier} onChange={e => setNewClient({ ...newClient, tier: e.target.value })} className="bg-[#F9F6EF] border border-[#333] text-white text-xs px-3 py-2 rounded" data-testid="new-client-tier">
              {TIER_OPTIONS.map(t => <option key={t} value={t}>{TIER_LABELS[t]}</option>)}
            </select>
            <input value={newClient.city} onChange={e => setNewClient({ ...newClient, city: e.target.value })} placeholder="City" className="bg-[#F9F6EF] border border-[#333] text-white text-xs px-3 py-2 rounded" data-testid="new-client-city" />
          </div>
          <button onClick={addClient} className="bg-[#2CA8A0] text-black text-xs font-medium px-4 py-2 rounded hover:bg-[#249089]" data-testid="confirm-add-btn">Create Client</button>
        </div>
      )}

      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-[#666] text-sm text-center py-8">No clients found.</p>}
        {filtered.map(c => <ClientCard key={c.client_id} client={c} onSave={handleSave} />)}
      </div>
    </div>
  );
}
